import React, { useState } from 'react';
import { Box } from '@mui/material';

import { useApp } from 'contexts/AppContext';

import { MultipleSelect, Select, TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';

import { flattenDataProviders } from '../../utils';
import { DomainPageActionBar } from '../DomainPageActionBar';
import { DomainPageSection as FormSection } from '../DomainPageSection';

export const DomainSetup = props => {
  const {
    onValidate,
    onClose,
    actionName,
    title,
    saveLabel,
    dataProviders: dataProvidersOptions,
    presetList,
    activableMedias
  } = props;

  const { domainConfig } = useApp();
  const initialState = {
    domain: '',
    name: '',
    dataProviders: [],
    excludeDataProviders: [],
    presetId: '',
    email: '',
    firstName: '',
    lastName: '',
    productAccess: [{ product: 'ermes', access: 'unlimited' }],
    selectedActivableMedias: domainConfig?.activableMedias || []
  };

  const ErrorState = {
    domainError: false,
    presetIdError: false,
    emailError: false,
    firstNameError: false,
    lastNameError: false,
    productAccessError: false
  };

  const [domainErrorMessage, setDomainErrorMessage] = useState('');

  const [
    {
      domain,
      name,
      dataProviders,
      excludeDataProviders,
      presetId,
      selectedActivableMedias
    },
    setState
  ] = useState(initialState);

  const resetStates = () => {
    setState(initialState);
  };

  const [{ domainError, presetIdError }, setError] = useState(ErrorState);

  const toggleError = (itemKey, state) => {
    setError(prevState => {
      const errors = { ...prevState };
      errors[`${itemKey}Error`] = state;
      return errors;
    });
  };

  const handleChange = itemKey => value => {
    const text = typeof value === 'string' ? value.trim() : value;
    toggleError(itemKey, !text);
    if (itemKey === 'domain') {
      const regex = /^[a-zA-Z0-9_]+$/;
      toggleError(itemKey, !regex.test(value));
    }
    setState(prevState => {
      const states = { ...prevState };
      states[itemKey] = text;
      return states;
    });
  };

  const doAction = async () => {
    const isEmptyDataProviders = dataProviders.length === 0;
    const isEmptyExcludeDataProviders = excludeDataProviders.length === 0;
    let error = false;
    if (domainError) {
      error = true;
    }
    const datas = {
      domain,
      name: name || domain,
      dataProviders: isEmptyDataProviders
        ? dataProviders
        : flattenDataProviders(dataProviders),
      excludeDataProviders: isEmptyExcludeDataProviders
        ? excludeDataProviders
        : flattenDataProviders(excludeDataProviders),
      presetId,
      activableMedias: selectedActivableMedias,
      groupTaxonomy: [
        {
          level: 'shop',
          name: 'Magasin',
          key: 'shop'
        }
      ]
    };
    setDomainErrorMessage('');
    if (!domain) {
      toggleError('domain', true);
      error = true;
    }
    if (!presetId) {
      toggleError('presetId', true);
      error = true;
    }
    if (!error) {
      try {
        await onValidate(datas);
        resetStates();
      } catch (err) {
        setDomainErrorMessage(
          'Cette key est déjà utilisée. Choisissez en une autre.'
        );
        toggleError('domain', true);
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 'clamp(83rem, 68vw, 106rem)',
        minWidth: 0,
        margin: 0,
        boxSizing: 'border-box',
        overflowX: 'clip'
      }}
    >
      <DomainPageActionBar
        title={title || 'Configuration du Domaine : Nouveau domaine'}
        saveLabel={saveLabel || `${actionName} le domaine`}
        showCreate={false}
        onCancel={() => {
          resetStates();
          onClose();
        }}
        onCreate={() => null}
        onSave={doAction}
      />
      <Container
        style={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: '0 0 1.5rem'
        }}
      >
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Nouveau domaine" xs={3}>
              <Container>
                <Row spacing={0} justify="flex-start" s>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Key du domaine"
                          required
                          placeholder="ERMES"
                          value={domain}
                          error={domainError}
                          errorText={domainErrorMessage}
                          onChange={handleChange('domain')}
                        />
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <TextField
                          title="Nom du domaine"
                          placeholder="Ermes"
                          value={name}
                          onChange={handleChange('name')}
                        />
                      </Item>
                    </Row>
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>

        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Sources de données" xs={3}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Container>
                          <Item justify="flex-start">
                            <MultipleSelect
                              title="Inclure"
                              small
                              placeHolder="Toutes les sources de données"
                              options={dataProvidersOptions}
                              selected={dataProviders.map(el => el.key)}
                              onChange={value => {
                                handleChange('dataProviders')(value);
                              }}
                            />
                          </Item>
                        </Container>
                      </Item>
                    </Row>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Container>
                          <Item justify="flex-start">
                            <MultipleSelect
                              title="Exclure"
                              small
                              placeHolder="Aucune source de données"
                              options={dataProvidersOptions}
                              selected={excludeDataProviders.map(el => el.key)}
                              onChange={value => {
                                handleChange('excludeDataProviders')(value);
                              }}
                            />
                          </Item>
                        </Container>
                      </Item>
                    </Row>
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>

        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Médias activables" xs={3}>
              <Row spacing={0} justify="flex-start">
                <Item xs={6} justify="flex-start">
                  <Item xs={11} justify="flex-start">
                    <MultipleSelect
                      small
                      required
                      selectAll
                      options={activableMedias.map(el => {
                        return { label: el.name, key: el.key };
                      })}
                      selected={selectedActivableMedias}
                      onChange={value => {
                        handleChange('selectedActivableMedias')(
                          value.map(el => el.key)
                        );
                      }}
                    />
                  </Item>
                </Item>
              </Row>
            </FormSection>
          </Item>
        </Row>

        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Preset" xs={3}>
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Row spacing={0} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Container>
                          <Item justify="flex-start">
                            <Select
                              required
                              options={presetList.map(preset => ({
                                value: preset._id,
                                label: preset.name
                              }))}
                              title="Preset"
                              value={presetId}
                              error={presetIdError}
                              onChange={handleChange('presetId')}
                            />
                          </Item>
                        </Container>
                      </Item>
                    </Row>
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
      </Container>
    </Box>
  );
};

export default DomainSetup;
